import React, { useState, useEffect } from 'react';
import {
  CampaignResponseItem,
  ResponseAnalytics,
  AIConfig,
  ConversationMessage,
  campaignsApi,
} from '@/services/campaignsApi';
import {
  MessageSquare,
  Bot,
  User,
  AlertTriangle,
  CheckCircle,
  Send,
  ArrowUpRight,
  Settings,
  BarChart3,
  Filter,
} from 'lucide-react';

interface CampaignResponsesPanelProps {
  campaignId: string;
  onClose: () => void;
}

type SentimentFilter = 'all' | 'positive' | 'negative' | 'neutral';
type ActiveView = 'list' | 'config';

const SENTIMENT_COLORS: Record<string, string> = {
  positive: 'bg-green-100 text-green-800',
  negative: 'bg-red-100 text-red-800',
  neutral: 'bg-yellow-100 text-yellow-800',
};

const CampaignResponsesPanel: React.FC<CampaignResponsesPanelProps> = ({
  campaignId,
  onClose,
}) => {
  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------
  const [responses, setResponses] = useState<CampaignResponseItem[]>([]);
  const [analytics, setAnalytics] = useState<ResponseAnalytics | null>(null);
  const [aiConfig, setAiConfig] = useState<AIConfig>({
    auto_reply_enabled: false,
    confidence_threshold: 0.7,
    escalation_rules: [],
    human_handoff_keywords: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [sentimentFilter, setSentimentFilter] = useState<SentimentFilter>('all');
  const [intentFilter, setIntentFilter] = useState<string>('all');
  const [processedFilter, setProcessedFilter] = useState<'all' | 'processed' | 'unprocessed'>('all');

  // UI state
  const [activeView, setActiveView] = useState<ActiveView>('list');
  const [expandedAIReply, setExpandedAIReply] = useState<string | null>(null);
  const [humanReplyTarget, setHumanReplyTarget] = useState<string | null>(null);
  const [humanReplyText, setHumanReplyText] = useState('');
  const [humanReplyChannel, setHumanReplyChannel] = useState<'sms' | 'email'>('sms');
  const [editingAIReply, setEditingAIReply] = useState<string | null>(null);
  const [editedAIText, setEditedAIText] = useState('');
  const [sendingReply, setSendingReply] = useState<string | null>(null);
  const [conversationMemberId, setConversationMemberId] = useState<string | null>(null);
  const [conversationMessages, setConversationMessages] = useState<ConversationMessage[]>([]);
  const [conversationLoading, setConversationLoading] = useState(false);

  // AI Config editing
  const [newHandoffKeyword, setNewHandoffKeyword] = useState('');

  // ---------------------------------------------------------------------------
  // Data loading
  // ---------------------------------------------------------------------------
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, any> = {};
      if (sentimentFilter !== 'all') params.sentiment = sentimentFilter;
      if (intentFilter !== 'all') params.intent = intentFilter;
      if (processedFilter === 'processed') params.is_processed = true;
      if (processedFilter === 'unprocessed') params.is_processed = false;

      const [responsesData, analyticsData] = await Promise.all([
        campaignsApi.listCampaignResponses(campaignId, params),
        campaignsApi.getResponseAnalytics(campaignId),
      ]);
      setResponses(responsesData);
      setAnalytics(analyticsData);
    } catch (err: any) {
      setError(err.message || 'Failed to load responses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [campaignId, sentimentFilter, intentFilter, processedFilter]);

  // Derive unique intents from responses
  const uniqueIntents = Array.from(
    new Set(responses.map((r) => r.ai_intent).filter(Boolean) as string[])
  );

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------
  const handleAcceptAIReply = async (responseItem: CampaignResponseItem) => {
    setSendingReply(responseItem.id);
    try {
      await campaignsApi.generateAIReply(campaignId, responseItem.id, true);
      await fetchData();
    } catch (err: any) {
      alert(err.message || 'Failed to send AI reply');
    } finally {
      setSendingReply(null);
    }
  };

  const handleEditAndSendAI = async (responseItem: CampaignResponseItem) => {
    setSendingReply(responseItem.id);
    try {
      await campaignsApi.sendHumanReply(
        campaignId,
        responseItem.id,
        editedAIText,
        'sms'
      );
      setEditingAIReply(null);
      setEditedAIText('');
      await fetchData();
    } catch (err: any) {
      alert(err.message || 'Failed to send edited reply');
    } finally {
      setSendingReply(null);
    }
  };

  const handleGenerateNewAIReply = async (responseId: string) => {
    setSendingReply(responseId);
    try {
      const result = await campaignsApi.generateAIReply(campaignId, responseId, false);
      setResponses((prev) =>
        prev.map((r) =>
          r.id === responseId
            ? { ...r, ai_suggested_reply: result.suggested_reply, ai_confidence: result.confidence }
            : r
        )
      );
    } catch (err: any) {
      alert(err.message || 'Failed to generate new AI reply');
    } finally {
      setSendingReply(null);
    }
  };

  const handleSendHumanReply = async (responseId: string) => {
    if (!humanReplyText.trim()) return;
    setSendingReply(responseId);
    try {
      await campaignsApi.sendHumanReply(campaignId, responseId, humanReplyText, humanReplyChannel);
      setHumanReplyTarget(null);
      setHumanReplyText('');
      await fetchData();
    } catch (err: any) {
      alert(err.message || 'Failed to send reply');
    } finally {
      setSendingReply(null);
    }
  };

  const handleEscalate = async (responseId: string) => {
    try {
      await campaignsApi.escalateResponse(campaignId, responseId, 'Manual escalation');
      await fetchData();
    } catch (err: any) {
      alert(err.message || 'Failed to escalate');
    }
  };

  const handleMarkProcessed = async (responseId: string) => {
    try {
      await campaignsApi.markResponseProcessed(campaignId, responseId);
      await fetchData();
    } catch (err: any) {
      alert(err.message || 'Failed to mark as processed');
    }
  };

  const handleOpenConversation = async (memberId: string) => {
    setConversationMemberId(memberId);
    setConversationLoading(true);
    try {
      const messages = await campaignsApi.getMemberConversation(campaignId, memberId);
      setConversationMessages(
        [...messages].sort(
          (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        )
      );
    } catch (err: any) {
      alert(err.message || 'Failed to load conversation');
    } finally {
      setConversationLoading(false);
    }
  };

  const handleSaveAIConfig = async () => {
    try {
      await campaignsApi.updateAIConfig(campaignId, aiConfig);
      alert('AI configuration saved');
    } catch (err: any) {
      alert(err.message || 'Failed to save AI config');
    }
  };

  const handleAddHandoffKeyword = () => {
    const trimmed = newHandoffKeyword.trim();
    if (trimmed && !aiConfig.human_handoff_keywords.includes(trimmed)) {
      setAiConfig((prev) => ({
        ...prev,
        human_handoff_keywords: [...prev.human_handoff_keywords, trimmed],
      }));
      setNewHandoffKeyword('');
    }
  };

  const handleRemoveHandoffKeyword = (keyword: string) => {
    setAiConfig((prev) => ({
      ...prev,
      human_handoff_keywords: prev.human_handoff_keywords.filter((k) => k !== keyword),
    }));
  };

  // ---------------------------------------------------------------------------
  // Render: Analytics Summary
  // ---------------------------------------------------------------------------
  const renderAnalyticsSummary = () => {
    if (!analytics) return null;
    return (
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <div className="bg-white rounded-lg p-3 border border-gray-200 text-center">
          <div className="text-2xl font-bold text-gray-900">{analytics.total_responses}</div>
          <div className="text-xs text-gray-500">Total Responses</div>
        </div>
        <div className="bg-green-50 rounded-lg p-3 border border-green-200 text-center">
          <div className="text-2xl font-bold text-green-700">
            {analytics.by_sentiment?.positive || 0}
          </div>
          <div className="text-xs text-green-600">Positive</div>
        </div>
        <div className="bg-red-50 rounded-lg p-3 border border-red-200 text-center">
          <div className="text-2xl font-bold text-red-700">
            {analytics.by_sentiment?.negative || 0}
          </div>
          <div className="text-xs text-red-600">Negative</div>
        </div>
        <div className="bg-yellow-50 rounded-lg p-3 border border-yellow-200 text-center">
          <div className="text-2xl font-bold text-yellow-700">
            {analytics.unprocessed_count}
          </div>
          <div className="text-xs text-yellow-600">Unprocessed</div>
        </div>
        <div className="bg-orange-50 rounded-lg p-3 border border-orange-200 text-center">
          <div className="text-2xl font-bold text-orange-700">
            {analytics.escalated_count}
          </div>
          <div className="text-xs text-orange-600">Escalated</div>
        </div>
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Render: Filter Bar
  // ---------------------------------------------------------------------------
  const renderFilterBar = () => (
    <div className="flex flex-wrap items-center gap-3 mb-4 bg-white rounded-lg p-3 border border-gray-200">
      <Filter className="h-4 w-4 text-gray-500" />

      <select
        value={sentimentFilter}
        onChange={(e) => setSentimentFilter(e.target.value as SentimentFilter)}
        className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <option value="all">All Sentiments</option>
        <option value="positive">Positive</option>
        <option value="negative">Negative</option>
        <option value="neutral">Neutral</option>
      </select>

      <select
        value={intentFilter}
        onChange={(e) => setIntentFilter(e.target.value)}
        className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <option value="all">All Intents</option>
        {uniqueIntents.map((intent) => (
          <option key={intent} value={intent}>
            {intent}
          </option>
        ))}
      </select>

      <select
        value={processedFilter}
        onChange={(e) =>
          setProcessedFilter(e.target.value as 'all' | 'processed' | 'unprocessed')
        }
        className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <option value="all">All</option>
        <option value="processed">Processed</option>
        <option value="unprocessed">Unprocessed</option>
      </select>

      {analytics && analytics.unprocessed_count > 0 && (
        <span className="ml-auto inline-flex items-center gap-1 bg-red-100 text-red-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
          <AlertTriangle className="h-3 w-3" />
          {analytics.unprocessed_count} unprocessed
        </span>
      )}
    </div>
  );

  // ---------------------------------------------------------------------------
  // Render: AI Reply Composer (inline)
  // ---------------------------------------------------------------------------
  const renderAIReplyComposer = (item: CampaignResponseItem) => {
    if (expandedAIReply !== item.id) return null;

    const isEditing = editingAIReply === item.id;
    const isSending = sendingReply === item.id;

    return (
      <div className="mt-3 bg-blue-50 rounded-lg p-3 border border-blue-200">
        <div className="flex items-center gap-2 mb-2">
          <Bot className="h-4 w-4 text-blue-600" />
          <span className="text-sm font-medium text-blue-800">AI Suggested Reply</span>
        </div>

        {isEditing ? (
          <div>
            <textarea
              value={editedAIText}
              onChange={(e) => setEditedAIText(e.target.value)}
              className="w-full text-sm border border-blue-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[80px]"
            />
            <div className="flex gap-2 mt-2">
              <button
                onClick={() => handleEditAndSendAI(item)}
                disabled={isSending || !editedAIText.trim()}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                <Send className="h-3 w-3" />
                {isSending ? 'Sending...' : 'Send Edited Reply'}
              </button>
              <button
                onClick={() => {
                  setEditingAIReply(null);
                  setEditedAIText('');
                }}
                className="px-3 py-1.5 text-xs text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div>
            <p className="text-sm text-gray-700 bg-white rounded p-2 border border-blue-100">
              {item.ai_suggested_reply || 'No AI suggestion available'}
            </p>
            <div className="flex gap-2 mt-2">
              <button
                onClick={() => handleAcceptAIReply(item)}
                disabled={isSending || !item.ai_suggested_reply}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-md hover:bg-green-700 disabled:opacity-50"
              >
                <CheckCircle className="h-3 w-3" />
                {isSending ? 'Sending...' : 'Accept & Send'}
              </button>
              <button
                onClick={() => {
                  setEditingAIReply(item.id);
                  setEditedAIText(item.ai_suggested_reply || '');
                }}
                disabled={!item.ai_suggested_reply}
                className="px-3 py-1.5 text-xs text-blue-600 bg-white border border-blue-300 rounded-md hover:bg-blue-50 disabled:opacity-50"
              >
                Edit
              </button>
              <button
                onClick={() => handleGenerateNewAIReply(item.id)}
                disabled={isSending}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs text-purple-600 bg-white border border-purple-300 rounded-md hover:bg-purple-50 disabled:opacity-50"
              >
                <Bot className="h-3 w-3" />
                {isSending ? 'Generating...' : 'Generate New'}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Render: Human Reply Composer (inline)
  // ---------------------------------------------------------------------------
  const renderHumanReplyComposer = (item: CampaignResponseItem) => {
    if (humanReplyTarget !== item.id) return null;

    const isSending = sendingReply === item.id;

    return (
      <div className="mt-3 bg-gray-50 rounded-lg p-3 border border-gray-200">
        <div className="flex items-center gap-2 mb-2">
          <User className="h-4 w-4 text-gray-600" />
          <span className="text-sm font-medium text-gray-800">Human Reply</span>
        </div>

        <textarea
          value={humanReplyText}
          onChange={(e) => setHumanReplyText(e.target.value)}
          placeholder="Type your reply..."
          className="w-full text-sm border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[80px]"
        />

        <div className="flex items-center gap-3 mt-2">
          <select
            value={humanReplyChannel}
            onChange={(e) => setHumanReplyChannel(e.target.value as 'sms' | 'email')}
            className="text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="sms">SMS</option>
            <option value="email">Email</option>
          </select>

          <button
            onClick={() => handleSendHumanReply(item.id)}
            disabled={isSending || !humanReplyText.trim()}
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            <Send className="h-3 w-3" />
            {isSending ? 'Sending...' : 'Send'}
          </button>

          <button
            onClick={() => {
              setHumanReplyTarget(null);
              setHumanReplyText('');
            }}
            className="px-3 py-1.5 text-xs text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Render: Response Card
  // ---------------------------------------------------------------------------
  const renderResponseCard = (item: CampaignResponseItem) => {
    const sentiment = item.ai_sentiment || 'neutral';
    const confidencePct = Math.round((item.ai_confidence || 0) * 100);

    return (
      <div
        key={item.id}
        className={`bg-white rounded-lg border p-4 mb-3 ${
          item.is_processed ? 'border-gray-200' : 'border-blue-300 shadow-sm'
        }`}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleOpenConversation(item.member_id)}
              className="text-sm font-semibold text-blue-600 hover:underline"
            >
              {item.member_name || 'Unknown Member'}
            </button>
            {item.member_phone && (
              <span className="text-xs text-gray-500">{item.member_phone}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {item.is_escalated && (
              <span className="inline-flex items-center gap-1 text-xs font-medium bg-orange-100 text-orange-800 px-2 py-0.5 rounded-full">
                <AlertTriangle className="h-3 w-3" />
                Escalated
              </span>
            )}
            {item.is_processed && (
              <span className="inline-flex items-center gap-1 text-xs font-medium bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
                <CheckCircle className="h-3 w-3" />
                Processed
              </span>
            )}
          </div>
        </div>

        {/* Response content */}
        <p className="text-sm text-gray-800 mb-3 bg-gray-50 rounded p-2">
          {item.response_content}
        </p>

        {/* Badges row */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span
            className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${
              SENTIMENT_COLORS[sentiment] || SENTIMENT_COLORS.neutral
            }`}
          >
            {sentiment}
          </span>

          {item.ai_intent && (
            <span className="inline-block text-xs font-medium bg-purple-100 text-purple-800 px-2 py-0.5 rounded-full">
              {item.ai_intent}
            </span>
          )}

          {/* AI Confidence meter */}
          <div className="flex items-center gap-1 ml-auto">
            <Bot className="h-3 w-3 text-gray-400" />
            <div className="w-20 bg-gray-200 rounded-full h-1.5">
              <div
                className={`h-1.5 rounded-full ${
                  confidencePct >= 70
                    ? 'bg-green-500'
                    : confidencePct >= 40
                    ? 'bg-yellow-500'
                    : 'bg-red-500'
                }`}
                style={{ width: `${confidencePct}%` }}
              />
            </div>
            <span className="text-xs text-gray-500">{confidencePct}%</span>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() =>
              setExpandedAIReply(expandedAIReply === item.id ? null : item.id)
            }
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100"
          >
            <Bot className="h-3 w-3" />
            AI Reply
          </button>

          <button
            onClick={() => {
              setHumanReplyTarget(humanReplyTarget === item.id ? null : item.id);
              setHumanReplyText('');
            }}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-md hover:bg-gray-100"
          >
            <User className="h-3 w-3" />
            Human Reply
          </button>

          {!item.is_escalated && (
            <button
              onClick={() => handleEscalate(item.id)}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded-md hover:bg-orange-100"
            >
              <ArrowUpRight className="h-3 w-3" />
              Escalate
            </button>
          )}

          {!item.is_processed && (
            <button
              onClick={() => handleMarkProcessed(item.id)}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-md hover:bg-green-100"
            >
              <CheckCircle className="h-3 w-3" />
              Mark Processed
            </button>
          )}
        </div>

        {/* Inline composers */}
        {renderAIReplyComposer(item)}
        {renderHumanReplyComposer(item)}
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Render: Conversation Thread
  // ---------------------------------------------------------------------------
  const renderConversationThread = () => {
    if (!conversationMemberId) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-blue-600" />
              <h3 className="font-semibold text-gray-900">Conversation Thread</h3>
            </div>
            <button
              onClick={() => {
                setConversationMemberId(null);
                setConversationMessages([]);
              }}
              className="text-gray-400 hover:text-gray-600 text-xl leading-none"
            >
              &times;
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {conversationLoading ? (
              <div className="text-center text-gray-500 py-8">Loading conversation...</div>
            ) : conversationMessages.length === 0 ? (
              <div className="text-center text-gray-500 py-8">No messages found</div>
            ) : (
              conversationMessages.map((msg) => {
                const isOutbound = msg.direction === 'outbound';
                return (
                  <div
                    key={msg.id}
                    className={`flex ${isOutbound ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[75%] rounded-lg p-3 ${
                        isOutbound
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-900'
                      }`}
                    >
                      <div className="flex items-center gap-1 mb-1">
                        {msg.ai_generated && (
                          <Bot className={`h-3 w-3 ${isOutbound ? 'text-blue-200' : 'text-gray-400'}`} />
                        )}
                        <span
                          className={`text-xs ${
                            isOutbound ? 'text-blue-200' : 'text-gray-500'
                          }`}
                        >
                          {msg.channel.toUpperCase()}
                          {msg.ai_generated ? ' (AI)' : ''}
                        </span>
                      </div>
                      <p className="text-sm">{msg.content}</p>
                      <div
                        className={`text-xs mt-1 ${
                          isOutbound ? 'text-blue-200' : 'text-gray-400'
                        }`}
                      >
                        {new Date(msg.timestamp).toLocaleString()}
                        {msg.status && ` - ${msg.status}`}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Render: AI Config Panel
  // ---------------------------------------------------------------------------
  const renderAIConfigPanel = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-gray-200 p-5">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Settings className="h-5 w-5 text-gray-600" />
          AI Configuration
        </h3>

        {/* Auto-reply toggle */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <label className="text-sm font-medium text-gray-900">Auto-Reply</label>
            <p className="text-xs text-gray-500">
              Automatically send AI-generated replies when confidence exceeds threshold
            </p>
          </div>
          <button
            onClick={() =>
              setAiConfig((prev) => ({
                ...prev,
                auto_reply_enabled: !prev.auto_reply_enabled,
              }))
            }
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              aiConfig.auto_reply_enabled ? 'bg-blue-600' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                aiConfig.auto_reply_enabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {/* Confidence threshold slider */}
        <div className="mb-6">
          <label className="text-sm font-medium text-gray-900 block mb-1">
            Confidence Threshold: {Math.round(aiConfig.confidence_threshold * 100)}%
          </label>
          <p className="text-xs text-gray-500 mb-2">
            Minimum AI confidence required for auto-reply
          </p>
          <input
            type="range"
            min={0}
            max={100}
            value={Math.round(aiConfig.confidence_threshold * 100)}
            onChange={(e) =>
              setAiConfig((prev) => ({
                ...prev,
                confidence_threshold: parseInt(e.target.value, 10) / 100,
              }))
            }
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
          />
          <div className="flex justify-between text-xs text-gray-400 mt-1">
            <span>0%</span>
            <span>50%</span>
            <span>100%</span>
          </div>
        </div>

        {/* Human handoff keywords */}
        <div className="mb-6">
          <label className="text-sm font-medium text-gray-900 block mb-1">
            Human Handoff Keywords
          </label>
          <p className="text-xs text-gray-500 mb-2">
            When a response contains any of these keywords, it will be flagged for human review
          </p>
          <div className="flex flex-wrap gap-2 mb-2">
            {aiConfig.human_handoff_keywords.map((keyword) => (
              <span
                key={keyword}
                className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 text-xs font-medium px-2.5 py-1 rounded-full"
              >
                {keyword}
                <button
                  onClick={() => handleRemoveHandoffKeyword(keyword)}
                  className="text-gray-400 hover:text-red-500 text-sm leading-none ml-1"
                >
                  &times;
                </button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={newHandoffKeyword}
              onChange={(e) => setNewHandoffKeyword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddHandoffKeyword();
                }
              }}
              placeholder="Add keyword..."
              className="flex-1 text-sm border border-gray-300 rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={handleAddHandoffKeyword}
              disabled={!newHandoffKeyword.trim()}
              className="px-3 py-1.5 bg-gray-800 text-white text-xs font-medium rounded-md hover:bg-gray-900 disabled:opacity-50"
            >
              Add
            </button>
          </div>
        </div>

        <button
          onClick={handleSaveAIConfig}
          className="w-full px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
        >
          Save Configuration
        </button>
      </div>
    </div>
  );

  // ---------------------------------------------------------------------------
  // Main Render
  // ---------------------------------------------------------------------------
  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Top bar */}
      <div className="flex items-center justify-between bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center gap-3">
          <MessageSquare className="h-5 w-5 text-blue-600" />
          <h2 className="text-lg font-semibold text-gray-900">Campaign Responses</h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setActiveView(activeView === 'config' ? 'list' : 'config')}
            className={`p-2 rounded-md ${
              activeView === 'config'
                ? 'bg-blue-100 text-blue-600'
                : 'text-gray-500 hover:bg-gray-100'
            }`}
            title="AI Settings"
          >
            <Settings className="h-5 w-5" />
          </button>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl leading-none px-2"
          >
            &times;
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-4">
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {activeView === 'config' ? (
          renderAIConfigPanel()
        ) : (
          <>
            {renderAnalyticsSummary()}
            {renderFilterBar()}

            {loading ? (
              <div className="text-center text-gray-500 py-12">Loading responses...</div>
            ) : responses.length === 0 ? (
              <div className="text-center text-gray-500 py-12">
                <MessageSquare className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                <p>No responses match the current filters</p>
              </div>
            ) : (
              <div>{responses.map((item) => renderResponseCard(item))}</div>
            )}
          </>
        )}
      </div>

      {/* Conversation Thread modal */}
      {renderConversationThread()}
    </div>
  );
};

export default CampaignResponsesPanel;
